import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { uiDateLocale } from '@/lib/i18n';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { StatusChip } from '@/components/ui/Chip';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { SlideOver, SlideOverFooter } from '@/components/ui/SlideOver';
import { Textarea } from '@/components/ui/Textarea';
import {
  useConsentTextVersions,
  useCreateConsentTextVersion,
  type ConsentTextVersionRow,
} from '@/hooks/useConsentRecords';
import {
  CONSENT_TYPES,
  consentTypeGroup,
  effectiveConsentTextVersion,
  sortConsentTextVersions,
  toDateInput,
  type ConsentTypeGroup,
  type ConsentTypeKey,
} from '@/lib/consent/consentTypes';
import { SectionCard, SettingsLoading, SettingsStack, SettingsSubpageHeader } from './settingsChrome';

const GROUP_ORDER: ConsentTypeGroup[] = ['information', 'marketing', 'health_law', 'refusal', 'treatment'];
const LABEL_MAX = 50;

interface EditorState {
  type: ConsentTypeKey;
  label: string;
  body: string;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(uiDateLocale(), { day: '2-digit', month: 'short', year: 'numeric' });
}

function isUniqueViolation(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { code?: string }).code === '23505';
}

export function ClinicConsentTexts() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();
  const versions = useConsentTextVersions(activeClinicId);
  const create = useCreateConsentTextVersion();
  const canEdit = hasPermission(activeClinicId, 'settings_general', 'update');

  const [expanded, setExpanded] = useState<ConsentTypeKey | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const byType = useMemo(() => {
    const map = new Map<ConsentTypeKey, ConsentTextVersionRow[]>();
    for (const type of CONSENT_TYPES) map.set(type, []);
    for (const v of versions.data ?? []) map.get(v.consent_type)?.push(v);
    for (const [type, rows] of map) map.set(type, sortConsentTextVersions(rows));
    return map;
  }, [versions.data]);

  if (!activeClinicId) return null;
  if (versions.isLoading) return <SettingsLoading />;
  if (versions.error) return <ErrorAlert message={t('settings.consent_texts.load_error')} />;

  const openEditor = (type: ConsentTypeKey, body: string, label = '') => {
    setSubmitted(false);
    setEditor({ type, body, label });
  };

  const publish = async (state: EditorState) => {
    await create.mutateAsync({
      p_clinic_id: activeClinicId,
      p_consent_type: state.type,
      p_version_label: state.label.trim(),
      p_body: state.body,
      p_language: 'da',
    });
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (!editor || !editor.label.trim() || !editor.body.trim()) return;
    try {
      await publish(editor);
      toast.success(t('settings.consent_texts.saved'));
      setEditor(null);
    } catch (err) {
      console.error('create_consent_text_version failed', err);
      toast.error(isUniqueViolation(err) ? t('settings.consent_texts.label_taken') : t('settings.consent_texts.save_error'));
    }
  };

  const adoptClinioText = async (global: ConsentTextVersionRow) => {
    const label = `${global.version_label} – ${toDateInput(new Date())}`.slice(0, LABEL_MAX);
    try {
      await publish({ type: global.consent_type, label, body: global.body });
      toast.success(t('settings.consent_texts.adopted'));
    } catch (err) {
      console.error('create_consent_text_version failed', err);
      toast.error(isUniqueViolation(err) ? t('settings.consent_texts.label_taken') : t('settings.consent_texts.save_error'));
    }
  };

  return (
    <SettingsStack>
      <SettingsSubpageHeader
        icon={<FileCheck className="h-5 w-5" aria-hidden />}
        title={t('settings.consent_texts.title')}
        subtitle={t('settings.consent_texts.subtitle')}
      />
      <Banner tone="info">{t('settings.consent_texts.default_notice')}</Banner>
      {!canEdit ? <Banner tone="warning">{t('settings.read_only_hint')}</Banner> : null}

      {GROUP_ORDER.map((group) => (
        <SectionCard
          key={group}
          title={t(`consent.groups.${group}`)}
          subtitle={t(`settings.consent_texts.group_hint.${group}`)}
        >
          <ul className="divide-y divide-ds-line">
            {CONSENT_TYPES.filter((type) => consentTypeGroup(type) === group).map((type) => {
              const rows = byType.get(type) ?? [];
              const effective = effectiveConsentTextVersion(rows, activeClinicId, type);
              const newestGlobal = rows.find((r) => r.clinic_id === null) ?? null;
              const usingClinio = effective !== null && effective.clinic_id === null;
              const isOpen = expanded === type;
              return (
                <li key={type} className="py-s3 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-center gap-s3">
                    <div className="min-w-0 flex-1">
                      <p className={cn(TYPE.body, 'text-ds-text')}>{t(`consent.types.${type}`)}</p>
                      <div className="mt-s1 flex flex-wrap items-center gap-s2">
                        {effective ? (
                          <>
                            <StatusChip tone={usingClinio ? 'upcoming' : 'completed'}>
                              {usingClinio ? t('consent.clinio_default') : t('consent.clinic_text')}
                            </StatusChip>
                            <span className={cn(TYPE.small, 'text-ds-muted')}>
                              {t('settings.consent_texts.in_force', {
                                label: effective.version_label,
                                date: formatDate(effective.created_at),
                              })}
                            </span>
                          </>
                        ) : (
                          <StatusChip tone="neutral">{t('settings.consent_texts.no_text')}</StatusChip>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-s2">
                      {rows.length > 0 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          aria-expanded={isOpen}
                          onClick={() => setExpanded(isOpen ? null : type)}
                        >
                          {isOpen ? t('settings.consent_texts.hide') : t('settings.consent_texts.show')}
                        </Button>
                      ) : null}
                      {canEdit && newestGlobal && !usingClinio ? (
                        <Button
                          type="button"
                          variant="tertiary"
                          loading={create.isPending && create.variables?.p_consent_type === type && !editor}
                          onClick={() => void adoptClinioText(newestGlobal)}
                        >
                          {t('settings.consent_texts.adopt')}
                        </Button>
                      ) : null}
                      {canEdit ? (
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => openEditor(type, effective?.body ?? '')}
                        >
                          {usingClinio ? t('settings.consent_texts.customize') : t('settings.consent_texts.new_version')}
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {isOpen ? (
                    <div className="mt-s3 space-y-s3">
                      {effective ? (
                        <div className="max-h-96 overflow-y-auto rounded-ctl border border-ds-line bg-ds-bg p-s3">
                          <p className={cn(TYPE.small, 'whitespace-pre-wrap text-ds-text')}>{effective.body}</p>
                        </div>
                      ) : null}
                      <div>
                        <p className={cn(TYPE.label, 'text-ds-text')}>
                          {t('settings.consent_texts.history', { count: rows.length })}
                        </p>
                        <ul className="mt-s2 space-y-s2">
                          {rows.map((row) => (
                            <li key={row.id} className="rounded-ctl border border-ds-line p-s3">
                              <details>
                                <summary className={cn(TYPE.small, 'flex cursor-pointer flex-wrap items-center gap-s2')}>
                                  <span className="font-medium text-ds-text">{row.version_label}</span>
                                  <span className="text-ds-muted">
                                    {row.clinic_id ? t('consent.clinic_text') : t('consent.clinio_default')}
                                    {' · '}
                                    {formatDate(row.created_at)}
                                  </span>
                                  {effective?.id === row.id ? (
                                    <StatusChip tone="completed">{t('settings.consent_texts.current')}</StatusChip>
                                  ) : null}
                                </summary>
                                <p className={cn(TYPE.small, 'mt-s2 whitespace-pre-wrap text-ds-text')}>{row.body}</p>
                              </details>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </SectionCard>
      ))}

      <SlideOver
        open={editor !== null}
        onClose={() => setEditor(null)}
        title={editor ? t('settings.consent_texts.editor_title', { type: t(`consent.types.${editor.type}`) }) : ''}
        description={t('settings.consent_texts.editor_description')}
        dirty={Boolean(editor?.label)}
        wide
        footer={
          <SlideOverFooter
            onCancel={() => setEditor(null)}
            primary={
              <Button onClick={() => void handleSave()} loading={create.isPending}>
                {t('settings.consent_texts.publish')}
              </Button>
            }
          />
        }
      >
        {editor ? (
          <div className="space-y-s4">
            <Input
              id="consent-text-label"
              label={t('settings.consent_texts.version_label')}
              hint={t('settings.consent_texts.version_label_hint')}
              maxLength={LABEL_MAX}
              value={editor.label}
              onChange={(e) => setEditor({ ...editor, label: e.target.value })}
              error={submitted && !editor.label.trim() ? t('settings.consent_texts.label_required') : undefined}
            />
            <Textarea
              id="consent-text-body"
              label={t('settings.consent_texts.body')}
              hint={t('settings.consent_texts.body_hint')}
              rows={18}
              value={editor.body}
              onChange={(e) => setEditor({ ...editor, body: e.target.value })}
              error={submitted && !editor.body.trim() ? t('settings.consent_texts.body_required') : undefined}
            />
          </div>
        ) : null}
      </SlideOver>
    </SettingsStack>
  );
}
